/**
 * A candidate whose account Join-Wonder has suspended, banned or deactivated.
 *
 * Their applications stay on record exactly as they were, but no company may
 * act on them -- no stage moves, interviews, offers or messages -- until the
 * account is reinstated. The API marks every company and admin row with
 * `candidate_account_status` and `candidate_restricted`, and refuses the
 * actions with a 409 (`code: "CANDIDATE_RESTRICTED"`). See "Restricted
 * candidates" in the backend's docs/api.md.
 */

/** The badge beside the status, by account status. */
export const RESTRICTION_LABELS = {
  SUSPENDED: 'Account suspended',
  BANNED: 'Account banned',
  INACTIVE: 'Account deactivated',
};

const RESTRICTION_WORDS = {
  SUSPENDED: 'suspended',
  BANNED: 'banned',
  INACTIVE: 'deactivated',
};

/** True when no company action is allowed on this row's candidate. */
export function isCandidateRestricted(row) {
  return Boolean(row?.candidate_restricted);
}

/** "suspended", "banned" or "deactivated". */
export function restrictionWord(row) {
  return RESTRICTION_WORDS[row?.candidate_account_status] ?? 'restricted';
}

/** The badge text for a restricted row, or null when the account is fine. */
export function restrictionLabel(row) {
  if (!isCandidateRestricted(row)) return null;
  return RESTRICTION_LABELS[row.candidate_account_status] ?? 'Account restricted';
}

/**
 * Why an action is unavailable, for a disabled button's tooltip -- the same
 * sentence the API answers with. Null when the action is allowed.
 */
export function blockedActionReason(row) {
  if (!isCandidateRestricted(row)) return null;
  return `This candidate's account is ${restrictionWord(row)}, so this application can't be changed.`;
}

/** Why the candidate cannot be messaged, or null when they can. */
export function blockedMessageReason(row) {
  if (!isCandidateRestricted(row)) return null;
  return `This candidate's account is ${restrictionWord(row)}, so they can't be messaged.`;
}

/** The banner at the top of the application. */
export function restrictionNotice(row) {
  if (!isCandidateRestricted(row)) return null;
  const word = restrictionWord(row);
  return (
    `This candidate's account has been ${word} by Join-Wonder. The application is kept on ` +
    "record, but it can't be moved forward, and no interviews or offers can be arranged " +
    `while the account is ${word}.`
  );
}
